/**
 * Where the core says "this should not have happened".
 *
 * Six places in the simulation used `console.error` for states that cannot
 * occur — an ant returning to a colony that has no record of it, a portal cell
 * that isn't there. Rule 3 of `docs/determinism.md` has always said *no console
 * in a simulation path*; the scanner never checked for one, and so it was the one
 * clause of the rule that was never enforced. Dropping the `DOM` library from
 * this package's `tsconfig` enforced it all at once, by taking the global away.
 *
 * A sink rather than a deletion, for two reasons.
 *
 * **A settlement worker wants them somewhere.** In a browser these six lines are
 * how you find out an ant transfer went wrong. On a server they should reach a
 * log, and the core has no business knowing which.
 *
 * **A test can install one and read it back.** This is the half that is new.
 * A `console.error` in a headless run prints into a void nobody reads, so an
 * impossible state that starts happening every hundredth tick looks exactly like
 * one that never happens. `packages/core/tests/purity.test.ts` installs a
 * recording sink and asserts it is empty after a district-minute, which turns
 * "should not happen" into something the suite actually checks.
 *
 * Module-level mutable state, deliberately: this *is* what `console` was, a
 * process-wide destination, and it is not simulation state. It affects no
 * decision, enters no hash and appears in no snapshot — a district that reported
 * an anomaly and one that swallowed it settle the same season. If that ever
 * stops being true, the anomaly belongs on the `EventBus` instead, where it can
 * be measured (Law 5).
 */

/** A destination for anomalies. `detail` is whatever context the site has. */
export type DiagnosticSink = (message: string, detail?: unknown) => void;

const IGNORE: DiagnosticSink = () => {};

let sink: DiagnosticSink = IGNORE;

/**
 * Point the core's anomaly reports somewhere.
 *
 * The client wires this to `console.error` at startup. Returns the previous
 * sink, so a test can install one and put the old one back.
 */
export function setDiagnosticSink(next: DiagnosticSink | null): DiagnosticSink {
  const previous = sink;
  sink = next ?? IGNORE;
  return previous;
}

/**
 * Report something that should not have happened.
 *
 * Never throws, whatever the sink does — a diagnostic that takes the simulation
 * down with it has made the world less deterministic rather than more, since
 * whether the tick completes would depend on who was listening. A test that
 * wants a throw installs a sink that records, and asserts after the run.
 */
export function anomaly(message: string, detail?: unknown): void {
  try {
    sink(message, detail);
  } catch {
    // Deliberately empty. See above.
  }
}
